/**
 * O catálogo do kickoff visto pela tela do administrador — e a regra de versão, dita em texto.
 *
 * O servidor é quem decide se uma edição é redação ou significado (`administracao.muda_significado`),
 * e a tela **não** oferece essa escolha de propósito: um seletor "criar versão nova?" seria o
 * caminho para alguém marcar "não" ao trocar as opções de uma pergunta, e as respostas já dadas
 * passariam a apontar para opção que não existe mais.
 *
 * O que mora aqui é a outra metade do contrato: traduzir o veredito que voltou numa frase que
 * quem editou entenda. `frase_do_efeito` é a única função do módulo que não fala com a API — e é
 * a que faz a diferença entre "salvo" e "salvo, e isto foi para quem já está respondendo".
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ErroDaApi, apagarJson, atualizarJson, editarJson, enviarJson, pedirJson } from "./api";

export const PERMISSAO_DO_CATALOGO_DO_KICKOFF = "questionario:gerenciar";

const RAIZ = "/questionarios";

export const CHAVE_DAS_TRILHAS = ["catalogo-do-kickoff"] as const;

export function chaveDaTrilha(chave: string) {
  return [...CHAVE_DAS_TRILHAS, chave] as const;
}

/* ------------------------------------------------------------------------ contrato */

const esquemaGatilho = z.object({
  operador: z.enum(["igual", "diferente", "em", "preenchida"]),
  valor: z.unknown().nullable().default(null),
  pergunta_id: z.string().nullable().default(null),
  externo: z.string().nullable().default(null),
});

const esquemaOpcao = z.object({
  id: z.string(),
  rotulo: z.string(),
  ajuda: z.string().nullable().default(null),
  visivel_se: esquemaGatilho.nullable().default(null),
});

const esquemaPergunta = z.object({
  id: z.string(),
  tipo: z.string(),
  enunciado: z.string(),
  ajuda: z.string().nullable().default(null),
  obrigatoria: z.boolean(),
  opcoes: z.array(esquemaOpcao).default([]),
  config: z.record(z.string(), z.unknown()).default({}),
  visivel_se: esquemaGatilho.nullable().default(null),
  e_tambem_se: z.array(esquemaGatilho).default([]),
  procedencia: z.string().nullable().default(null),
});

const esquemaTrilhaNaLista = z.object({
  chave: z.string(),
  nome: z.string(),
  escopo: z.string(),
  ativo: z.boolean(),
  versao: z.number(),
  perguntas: z.number(),
});

const esquemaTrilha = z.object({
  chave: z.string(),
  nome: z.string(),
  escopo: z.string(),
  ativo: z.boolean(),
  notas: z.string().nullable(),
  versao: z.number(),
  versoes_vivas: z.array(z.number()),
  perguntas: z.array(esquemaPergunta),
});

const esquemaEfeito = z.object({
  versao_nova: z.boolean(),
  versao: z.number(),
  versoes_corrigidas: z.array(z.number()).default([]),
});

export type GatilhoDaPergunta = z.infer<typeof esquemaGatilho>;
export type OpcaoDaPergunta = z.infer<typeof esquemaOpcao>;
export type PerguntaDoCatalogo = z.infer<typeof esquemaPergunta>;
export type TrilhaNaLista = z.infer<typeof esquemaTrilhaNaLista>;
export type TrilhaDoCatalogo = z.infer<typeof esquemaTrilha>;
export type EfeitoDaEdicao = z.infer<typeof esquemaEfeito>;

/* -------------------------------------------------------------------------- leitura */

export function useTrilhasDoCatalogo() {
  return useQuery<TrilhaNaLista[]>({
    queryKey: CHAVE_DAS_TRILHAS,
    queryFn: ({ signal }) => pedirJson(RAIZ, z.array(esquemaTrilhaNaLista), { signal }),
  });
}

export function useTrilhaDoCatalogo(chave: string) {
  return useQuery<TrilhaDoCatalogo>({
    queryKey: chaveDaTrilha(chave),
    enabled: chave.length > 0,
    queryFn: ({ signal }) =>
      pedirJson(`${RAIZ}/${encodeURIComponent(chave)}`, esquemaTrilha, { signal }),
  });
}

/* -------------------------------------------------------------------------- escrita */

/* Toda mutação invalida a trilha E a lista: a lista mostra o número da versão vigente, e uma
   publicação que não a invalidasse deixaria a tela anterior dizendo "versão 1" sobre uma trilha
   que já está na 2 — o mesmo tipo de segunda lista envelhecida em silêncio que o cadastro de
   Recursos Plus já tinha. */
function invalidar(cliente: ReturnType<typeof useQueryClient>, chave: string) {
  void cliente.invalidateQueries({ queryKey: chaveDaTrilha(chave) });
  void cliente.invalidateQueries({ queryKey: CHAVE_DAS_TRILHAS });
}

export function useAlterarPergunta(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (pergunta: PerguntaDoCatalogo) =>
      editarJson(
        `${RAIZ}/${encodeURIComponent(chave)}/perguntas/${encodeURIComponent(pergunta.id)}`,
        esquemaEfeito,
        pergunta,
      ),
    onSuccess: () => invalidar(cliente, chave),
  });
}

export function useIncluirPergunta(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { pergunta: PerguntaDoCatalogo; depois_de: string | null }) =>
      enviarJson(`${RAIZ}/${encodeURIComponent(chave)}/perguntas`, esquemaEfeito, corpo),
    onSuccess: () => invalidar(cliente, chave),
  });
}

export function useRemoverPergunta(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (perguntaId: string) =>
      apagarJson(
        `${RAIZ}/${encodeURIComponent(chave)}/perguntas/${encodeURIComponent(perguntaId)}`,
        esquemaEfeito,
      ),
    onSuccess: () => invalidar(cliente, chave),
  });
}

export function useReordenarTrilha(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ordem: string[]) =>
      atualizarJson(`${RAIZ}/${encodeURIComponent(chave)}/ordem`, esquemaEfeito, { ordem }),
    onSuccess: () => invalidar(cliente, chave),
  });
}

const esquemaEfeitoDaPublicacao = z.object({ publicada: z.boolean(), mudou: z.boolean() });

/**
 * Publicar ou despublicar a trilha — defeito 3.4 da spec de 28/09: o catálogo mostrava
 * "despublicada" e não havia como mudar. Despublicar não afeta quem já está respondendo; o
 * servidor recusa publicar trilha sem pergunta, e a recusa chega com o motivo.
 */
export function useDefinirPublicacao(chave: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (publicada: boolean) =>
      atualizarJson(`${RAIZ}/${encodeURIComponent(chave)}/publicacao`, esquemaEfeitoDaPublicacao, {
        publicada,
      }),
    onSuccess: () => invalidar(cliente, chave),
  });
}

/* ------------------------------------------------------------------------ vocabulário */

export const ROTULO_DO_TIPO: Record<string, string> = {
  texto_curto: "Texto curto",
  texto_longo: "Texto longo",
  sim_nao: "Sim/Não",
  escolha_unica: "Escolha única",
  escolha_multipla: "Escolha múltipla",
  outro: "Campo “Outro”",
  contato: "Contato",
  lista_repetivel: "Lista repetível",
  upload: "Arquivo",
  cor: "Cor",
  fonte: "Fonte",
  galeria: "Galeria",
  url: "Endereço web",
  numero: "Número",
  bloco_informativo: "Bloco informativo",
  credencial: "Credencial",
};

/** Os tipos que a tela oferece ao incluir uma pergunta.
 *
 * `credencial` fica de fora: ela não é campo de formulário comum — o valor vai para o cofre e a
 * resposta guarda só a referência ao segredo. Criar uma pela tela do catálogo, sem a trilha de
 * infraestrutura em volta, produziria uma pergunta que ninguém sabe responder.
 */
export const TIPOS_OFERECIDOS = [
  "texto_curto",
  "texto_longo",
  "sim_nao",
  "escolha_unica",
  "escolha_multipla",
  "numero",
  "url",
  "upload",
  "bloco_informativo",
] as const;

export const TIPOS_COM_OPCOES = new Set(["escolha_unica", "escolha_multipla", "galeria"]);

/**
 * A frase que a tela mostra depois de salvar — a tradução do veredito do servidor.
 *
 * Ela existe porque as duas respostas possíveis significam coisas muito diferentes para quem
 * acabou de clicar, e "salvo" esconderia as duas: uma correção de redação **alcança quem está
 * respondendo agora**, e uma mudança de significado **não alcança** — quem já começou continua
 * na versão em que entrou. Sem dizer isso, a próxima pergunta de quem edita é sempre a mesma:
 * "então o cliente já está vendo?".
 */
export function fraseDoEfeito(efeito: EfeitoDaEdicao): string {
  if (efeito.versao_nova) {
    return (
      `Publicada a versão ${efeito.versao}. Quem ainda não abriu a trilha responde a partir ` +
      "dela; quem já está preenchendo continua na versão em que entrou."
    );
  }
  if (efeito.versoes_corrigidas.length === 0) {
    return "Nada mudou: o texto enviado é igual ao que já estava lá.";
  }
  if (efeito.versoes_corrigidas.length === 1) {
    return `Texto corrigido na versão ${efeito.versoes_corrigidas[0]}, valendo agora.`;
  }
  const lista = efeito.versoes_corrigidas.join(", ");
  return (
    `Texto corrigido nas versões ${lista} — inclusive nas antigas que alguém ainda está ` +
    "respondendo, para ninguém ficar lendo a redação errada até enviar."
  );
}

export function respostasDaCondicao(pergunta: PerguntaDoCatalogo) {
  if (pergunta.tipo === "sim_nao") return [
    { chave: "true", valor: true, rotulo: "Sim" },
    { chave: "false", valor: false, rotulo: "Não" },
  ];
  return pergunta.opcoes.map((opcao) => ({ chave: opcao.id, valor: opcao.id, rotulo: opcao.rotulo }));
}

export function fraseDaCondicao(gatilho: GatilhoDaPergunta, perguntas: PerguntaDoCatalogo[]): string {
  const indice = perguntas.findIndex((item) => item.id === gatilho.pergunta_id);
  const origem = perguntas[indice];
  const resposta = origem ? respostasDaCondicao(origem).find((item) => item.valor === gatilho.valor)?.rotulo : null;
  const operador = { igual: "=", diferente: "≠", em: "entre", preenchida: "preenchida" }[gatilho.operador];
  const valor = resposta ?? (typeof gatilho.valor === "boolean" ? (gatilho.valor ? "Sim" : "Não") : JSON.stringify(gatilho.valor));
  return `aparece quando ${origem ? `${indice + 1}` : gatilho.externo ?? gatilho.pergunta_id} ${operador}${gatilho.operador === "preenchida" ? "" : ` ${valor}`}`;
}



export function problemasDaEdicao(causa: unknown): string[] {
  if (!(causa instanceof ErroDaApi)) return [];
  const detalhe = z.object({ problemas: z.array(z.string()) }).safeParse(causa.detalhe);
  return detalhe.success ? detalhe.data.problemas : [];
}
